import React from "react";
import { Icon } from "../components/tailwind";
import { Props as IconProps } from "../components/tailwind/icon/Icon";

export type IconActionProps = IconProps & {
  "data-balloon"?: string;
  "data-balloon-pos"?: string;
};

export type IconAction = {
  type: "lock-segment" | "add-to-translation-memory";
  commonIconProps?: Partial<IconProps>;
  iconProps: IconActionProps[];
};

export type IconVariantState = {
  ["lock-segment"]: 0 | 1 | 2;
  ["add-to-translation-memory"]?: 0 | 1 | 2;
};

export type AllowedIconActions = typeof SegmentActions[keyof typeof SegmentActions]["type"];

const ToggleLockAction: IconAction = {
  type: "lock-segment",
  commonIconProps: {
    className: "tw-bg-inherit tw-leading-[1] tw-text-lg tw-cursor-pointer",
  },
  iconProps: [
    {
      name: "lock_open",
      "data-balloon": "Lock segment",
      "data-balloon-pos": "left",
    },
    {
      name: "lock",
      "data-balloon": "Unlock segment",
      "data-balloon-pos": "left",
    },
    {
      name: "lock",
      className: "tw-text-gray-500 tw-cursor-default tw-pointer-events-none",
    },
  ],
};

const AddToTMAction: IconAction = {
  type: "add-to-translation-memory",
  iconProps: [
    {
      name: "check",
      "data-balloon": "Add to translation memory",
      "data-balloon-pos": "left",
      className: "tw-bg-inherit tw-leading-[1] tw-text-lg tw-cursor-pointer",
    },
    {
      name: "check",
      "data-balloon": "Already in translation memory",
      "data-balloon-pos": "left",
      className:
        "tw-bg-inherit tw-leading-[1] tw-text-lg tw-cursor-default tw-pointer-events-none tw-text-green-500",
    },
    {
      name: "check",
      "data-balloon": "Provide Translation before adding to translation memory",
      "data-balloon-pos": "left",
      className:
        "tw-bg-inherit tw-leading-[1] tw-text-lg tw-cursor-default tw-text-gray-500",
    },
  ],
};

export const SegmentActions = {
  [ToggleLockAction.type]: ToggleLockAction,
  [AddToTMAction.type]: AddToTMAction,
} as const;

type Props = {
  onAction: (action: AllowedIconActions) => void;
  allowedActions: AllowedIconActions[];
  showIconVariants: IconVariantState;
  index: number;
};

export const TranslationViewSegmentActions: React.FC<Props> = ({
  onAction,
  allowedActions,
  showIconVariants,
  index,
}) => {
  const getAction = (actionType: AllowedIconActions): IconAction => {
    return SegmentActions[actionType];
  };
  const getIconProps = (actionType: AllowedIconActions): IconProps => {
    const action = getAction(actionType);
    const variantIndex = showIconVariants?.[actionType] || 0;
    if (action.commonIconProps) {
      let mergedClassNames = action.commonIconProps?.className || "";
      if (action.iconProps[variantIndex].className) {
        mergedClassNames += ` ${action.iconProps[variantIndex].className}`;
      }

      return {
        ...action.iconProps[variantIndex],
        className: mergedClassNames,
      };
    }
    return action.iconProps[variantIndex];
  };

  return (
    <div
      className="tw-flex tw-gap-1 tw-items-center tw-pr-2"
      data-testid={`translation-view-segment-actions-${index}`}
    >
      {allowedActions.map((actionType) => {
        const iconProps = getIconProps(actionType);
        return (
          <Icon
            key={actionType}
            onClick={() => onAction(actionType)}
            data-testid={`translation-view-segment-action-${actionType}-${index}`}
            {...iconProps}
          />
        );
      })}
    </div>
  );
};
